import React, { useEffect, useState } from 'react';
import TableCompo from '../MainScreens/Table/TableCompo';
import { useDispatch, useSelector } from 'react-redux';
import { CardContent, Box, Typography } from '@mui/material';
import { fetchBenefitsRequest } from '../../redux/Order/RedeemOrdersSlice';
import { toast } from 'react-toastify';
import { updateBenefitStatusRequest } from '../../redux/settings/settingsSlice';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from '@mui/material';
import useCsvConverter from '../../hooks/convertToCSV';
import { apiRequest } from '../../redux/Reports/reportsSlice';
import { canShowPermission } from '../MainScreens/Permissions/permissionObject';

const ReferralOrdersTable = () => {
  const dispatch = useDispatch();
  const {
    benefitsData = [],
    totalBenefits = 0,
    error,
    lastAction,
  } = useSelector((state) => state.redeemOrders || {});

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const stored = localStorage.getItem('userPermissions');
    if (stored) {
      try {
        setPermissions(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse userPermissions', e);
      }
    }
  }, []);
  const canEdit = canShowPermission(permissions, 'referralBenefits', 'edit');

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleStatusClick = (row) => {
    setSelectedBenefit(row);
    setDialogOpen(true);
  };
  const handleConfirmStatusChange = () => {
    if (!selectedBenefit) return;

    const payload = {
      benefitId: selectedBenefit._id,
      isActive: !selectedBenefit.isActive,
    };
    dispatch(updateBenefitStatusRequest(payload));

    setDialogOpen(false);
    setSelectedBenefit(null);
  };

  useEffect(() => {
    dispatch(fetchBenefitsRequest({ start: page * limit, limit, search }));
  }, [dispatch, page, limit, search]);

  useEffect(() => {
    if (error && lastAction === 'fetchReferralOrders') {
      toast.error(error);
    }
  }, [error, lastAction]);

  const handleTableChange = (action, tableState) => {
    switch (action) {
      case 'changePage':
        setPage(tableState.page);
        dispatch(
          fetchBenefitsRequest({
            start: tableState.page * tableState.rowsPerPage,
            limit: tableState.rowsPerPage,
            search,
          }),
        );
        break;

      case 'changeRowsPerPage':
        setLimit(tableState.rowsPerPage);
        setPage(0);
        dispatch(
          fetchBenefitsRequest({
            start: 0,
            limit: tableState.rowsPerPage,
            search,
          }),
        );
        break;

      case 'search':
        const searched = tableState.searchText || '';
        setSearch(searched);
        setPage(0);
        dispatch(fetchBenefitsRequest({ start: 0, limit, search: searched }));
        break;

      default:
        break;
    }
  };

  const columns = [
    {
      field: 'startDate',
      headerName: 'Start Date',
      render: (val, row) =>
        row.startDate
          ? new Date(row.startDate).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : 'N.A',
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      render: (val, row) =>
        row.endDate
          ? new Date(row.endDate).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : 'N.A',
    },
    {
      field: 'validity',
      headerName: 'Validity',
      render: (val, row) => {
        if (row.validityDays !== undefined && row.validityDays !== null) {
          return `${row.validityDays} day${Number(row.validityDays) > 1 ? 's' : ''}`;
        }
        if (row.validTill) {
          return new Date(row.validTill).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          });
        }
        return 'N.A';
      },
    },
    {
      field: 'referralType',
      headerName: 'Referral Type',
    },
    {
      field: 'transactionsFrequency',
      headerName: 'Transaction Frequency',
    },
    {
      field: 'transactionsType',
      headerName: 'Transaction Type',
    },
    {
      field: 'buyMetal',
      headerName: 'Buy Metal',
      render: (val, row) => {
        const buyMetal = row?.buyMetal;
        if (!buyMetal) return 'N.A';

        const metalName = buyMetal.metalName || '';
        const karat = buyMetal.karat ? `${buyMetal.karat}K` : '';
        const purity = buyMetal.purity?.toString() || '';

        const combined = [metalName, karat, purity].filter(Boolean).join(' ');

        return combined || 'N.A';
      },
    },
    {
      field: 'bullion',
      headerName: 'Reward Metal',
      render: (val, row) => {
        const bullion = row?.bullion;
        if (!bullion) return 'N.A';

        const metalName = bullion.metalName || '';
        const karat = bullion.karat ? `${bullion.karat}K` : '';
        const purity = bullion.purity?.toString() || '';

        const combined = [metalName, karat, purity].filter(Boolean).join(' ');

        return combined || row.metalName;
      },
    },
    {
      field: 'diamondPointType',
      headerName: 'Diamond Point Type',
      render: (val, row) => row.diamondPointType || 'N.A',
    },
    {
      field: 'diamondPointValue',
      headerName: 'Diamond Point Value',
      render: (val, row) =>
        row.diamondPointValue !== undefined && row.diamondPointValue !== null
          ? row.diamondPointValue
          : 'N.A',
    },
    {
      field: 'joinerBullion',
      headerName: 'Joiner Metal',
      render: (val, row) => {
        const joinerBullion = row?.joinerBullion;
        if (!joinerBullion) return 'N.A';

        const metalName = joinerBullion.metalName || '';
        const karat = joinerBullion.karat ? `${joinerBullion.karat}K` : '';
        const purity = joinerBullion.purity?.toString() || '';

        const combined = [metalName, karat, purity].filter(Boolean).join(' ');

        return combined || row.metalName;
      },
    },
    {
      field: 'points',
      headerName: 'Points',
      render: (v, row) => row.points ?? 'N.A',
    },
    {
      field: 'percentage',
      headerName: 'Percentage (%)',
      render: (v, row) => row.percentage ?? 'N.A',
    },
    {
      field: 'pointsPerGram',
      headerName: 'Referral Weight',
      render: (v, row) => row.pointsPerGram ?? 'N.A',
    },
    {
      field: 'joinerPointsPerGram',
      headerName: 'Joiner Weight',
      render: (v, row) => row.joinerPointsPerGram ?? 'N.A',
    },
    {
      field: 'isActive',
      headerName: 'Status',
      render: (v, row) =>
        canEdit ? (
          <span
            onClick={() => handleStatusClick(row)}
            style={{
              color: row.isActive ? 'green' : 'red',
              fontWeight: 'bold',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            {row.isActive ? 'Enable' : 'Disable'}
          </span>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 40,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: row.isActive ? 'green' : 'red',
                textAlign: 'center',
              }}
            >
              {row.isActive ? 'Enable' : 'Disable'}
            </Typography>
          </Box>
        ),
    },
  ];

  const { convertToCsv } = useCsvConverter();

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadCsv = () => {
    console.log('Store CSV API called');

    dispatch(
      apiRequest({
        method: 'GET',
        endpoint: '/api/v1/referral/referralBenefitCsv',
        query: {},
        callback: (response) => {
          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, 'referral.csv');
        },
      }),
    );
  };

  return (
    <Box sx={{ mt: 4, background: '#fff', borderRadius: '12px', boxShadow: 2 }}>
      <Typography variant="h6" sx={{ p: 2 }}>
        Referral Orders
      </Typography>
      <CardContent>
        <TableCompo
          columns={columns}
          data={benefitsData || []}
          totalCount={totalBenefits}
          page={page}
          limit={limit}
          onTableChange={handleTableChange}
          extraButtons={
            <Button size="sm" variant="outlined" onClick={handleDownloadCsv}>
              Download CSV
            </Button>
          }
        />
      </CardContent>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          Are you sure you want to{' '}
          {selectedBenefit?.isActive ? 'disable' : 'enable'} this benefit?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="secondary">
            No
          </Button>
          <Button onClick={handleConfirmStatusChange} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReferralOrdersTable;
